import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { EngagementType } from '../../graphqlEnums'

export const UndismissEngagementMutationGQL = graphql(/* GraphQL */ `
  mutation UndismissEngagement($input: UndismissEngagementInput!) {
    undismissEngagement(input: $input) {
      __typename
      ... on UndismissEngagementSuccess {
        engagement {
          __typename
          id
          status
          dismissedAt
          updatedAt
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UndismissEngagementArgs = {
  engagementId: string
  engagementType: EngagementType
}

type UseUndismissEngagementResult = {
  undismissEngagement: (args: UndismissEngagementArgs) => Promise<boolean>
  loading: boolean
}

/**
 * Wraps the unified `undismissEngagement` mutation, restoring a dismissed
 * item to `unreplied`. Kind-agnostic — accepts `mention` or `comment` via
 * `engagementType`. Sibling of `useDismissEngagement`; mirrors its shape so
 * the eventual comments cutover slots in cleanly.
 */
export const useUndismissEngagement = (): UseUndismissEngagementResult => {
  const [mutate, { loading }] = useTypedMutation(
    UndismissEngagementMutationGQL,
    (data) => data.undismissEngagement,
    {
      successTypename: 'UndismissEngagementSuccess',
      // Reopening a mention returns it to `unreplied`; refresh the counts.
      refetchQueries: ['MentionCounts'],
    },
  )

  const undismissEngagement = async ({
    engagementId,
    engagementType,
  }: UndismissEngagementArgs): Promise<boolean> => {
    // The hook is kind-agnostic, so the toast copy has to follow.
    const successMessage =
      engagementType === 'comment'
        ? 'Comment reopened successfully'
        : 'Mention reopened successfully'
    const errorMessage =
      engagementType === 'comment'
        ? 'An error occurred while reopening the comment'
        : 'An error occurred while reopening the mention'

    const result = await mutate({
      variables: {
        input: { engagementId, engagementType },
      },
    })

    if (result.success) {
      toast.success(successMessage, { duration: 1_000 })
      return true
    }

    toast.error(errorMessage, { duration: 2_000 })
    return false
  }

  return {
    undismissEngagement,
    loading,
  }
}
